import { Card, Button } from '@suscri/ui';
import {
  Settings,
  Shield,
  LogOut,
  TrendingUp,
  CreditCard,
  Mail,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function AdminPage() {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleUsers = () => {
    navigate('/admin/users');
  };

  return (
    <div className="app-shell min-h-screen">
      {/* Header */}
      <div className="border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-4 py-5 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Panel de Administración
                </h1>
                <p className="text-sm text-gray-600">Gestión del sistema</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2 hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Métricas del negocio */}
          <Card
            className="group relative overflow-hidden bg-gradient-to-br from-white to-gray-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-xl p-6 cursor-pointer"
            onClick={() => navigate('/admin/metrics')}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Métricas
                  </h3>
                  <p className="text-sm text-gray-600">KPIs del negocio SaaS</p>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Conversión, activación, consumo de créditos, MRR, ARPU, plazas
                fundador y acciones más usadas.
              </p>
            </div>
          </Card>

          {/* Pagos (Stripe) */}
          <Card
            className="group relative overflow-hidden bg-gradient-to-br from-white to-gray-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-xl p-6 cursor-pointer"
            onClick={() => navigate('/admin/stripe')}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <CreditCard className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Pagos (Stripe)
                  </h3>
                  <p className="text-sm text-gray-600">
                    Claves y estado de la pasarela
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Guarda las claves de Stripe para activar el cobro real de planes
                y packs. Guía: docs/ALTA-STRIPE.md.
              </p>
            </div>
          </Card>

          {/* Email (SMTP) */}
          <Card
            className="group relative overflow-hidden bg-gradient-to-br from-white to-gray-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-xl p-6 cursor-pointer"
            onClick={() => navigate('/admin/email')}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Email (SMTP)
                  </h3>
                  <p className="text-sm text-gray-600">
                    Servidor de correo saliente
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Configura el SMTP para enviar bienvenida, recuperación de
                contraseña y alertas.
              </p>
            </div>
          </Card>

          {/* Configuración del Sistema */}
          <Card className="group relative overflow-hidden bg-gradient-to-br from-white to-gray-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-xl p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Settings className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Configuración del Sistema
                  </h3>
                  <p className="text-sm text-gray-600">
                    Configuraciones generales del sistema
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Próximamente: Configuraciones generales, variables de entorno y
                parámetros del sistema.
              </p>
            </div>
          </Card>

          {/* Usuarios y Permisos */}
          <Card
            onClick={handleUsers}
            className="group relative overflow-hidden bg-gradient-to-br from-white to-gray-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-xl p-6 cursor-pointer"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Usuarios y Permisos
                  </h3>
                  <p className="text-sm text-gray-600">
                    Gestión de usuarios y roles
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Gestiona usuarios, roles y permisos.
              </p>
            </div>
          </Card>
        </div>

        {/* Información del Sistema */}
        <Card className="group relative overflow-hidden bg-gradient-to-br from-white to-gray-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-xl p-6 mt-8">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative z-10">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Información del Sistema
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 hover:shadow-md transition-shadow">
                <div className="text-3xl font-bold text-blue-600 mb-2">1</div>
                <div className="text-sm font-semibold text-gray-700">
                  Módulos Activos
                </div>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 hover:shadow-md transition-shadow">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  Online
                </div>
                <div className="text-sm font-semibold text-gray-700">
                  Estado del Sistema
                </div>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl border border-yellow-200 hover:shadow-md transition-shadow">
                <div className="text-3xl font-bold text-yellow-600 mb-2">
                  v1.0
                </div>
                <div className="text-sm font-semibold text-gray-700">
                  Versión
                </div>
              </div>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}

export default AdminPage;
